/**
 * WEB-4089 — права текущего проекта в оболочке кабинета: один запрос
 * `GET /projects/:id/members/my-role` на каждый выбранный проект (критерии
 * владельца 02.11: кабинет спрашивает права у сервера один раз, при выборе
 * проекта; освежает только при смене проекта, перезагрузке и входе).
 * Монтируется в `router/DashboardLayout.tsx` рядом с `useShellBalance`: все
 * страницы проекта — дети оболочки, поэтому прямой заход на любую из них
 * получает права сервера, а не догадку по роли из списка проектов
 * (`hooks/useProjectRole.ts`). Прежде права грузили три страницы сами
 * («ИИ-студия», «Подписки», «Домены»).
 *
 * Без выбранного проекта (режим организации) запроса нет. После центрального
 * сброса среза при том же проекте права спрашиваются снова (по метке
 * `myRoleRequestedProjectId`). Поздний ответ — не на последний запрос — права
 * не меняет: это держит срез (`myRoleRequestId` в
 * `store/projectMembersSlice.ts`).
 * Другие места права проекта не грузят — сторож
 * `projectRightsDoor.web4089.guard.test.ts` (кроме перечитывания после
 * передачи владения в `pages/ProjectSettings.tsx`).
 */
import { useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '@/hooks/hooks';
import { fetchMyRoleThunk } from '@/store/projectMembersSlice';

export function useShellProjectRights(): void {
  const dispatch = useAppDispatch();
  const projectId = useAppSelector(
    (state) => state.projects.currentProject?.id,
  );
  // Возврат 1: центральный сброс срезов (store/index.ts) обнуляет права и
  // метку запроса, а номер проекта может остаться тем же (повторный выбор
  // того же проекта, новый проект из баннера). Поэтому судим не по смене
  // номера, а по метке: права выбранного проекта ещё не запрошены — спросить.
  // Отказ метку не снимает — повторного запроса (петли) нет.
  const requested = useAppSelector(
    (state) => state.projectMembers.myRoleRequestedProjectId ?? null,
  );
  useEffect(() => {
    if (projectId && requested !== projectId)
      void dispatch(fetchMyRoleThunk(projectId));
  }, [dispatch, projectId, requested]);
}
